import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as QRCode from "qrcode";
import Footer from "../../components/Footer/Footer";
import NavForm from "../../components/Navbar/NavForm";
import configData from "../../config/config.json";
import appStoreBadge from "../../img/AppStore.png";
import playStoreBadge from "../../img/GooglePlay.png";
import "./DownloadScreen.css";

const detectPlatform = () => {
  if (typeof window === "undefined") {
    return "unknown";
  }

  const userAgent =
    window.navigator.userAgent || window.navigator.vendor || window.opera || "";
  const isAndroid = /android/i.test(userAgent);
  const isIOS =
    /iPad|iPhone|iPod/i.test(userAgent) ||
    (window.navigator.platform === "MacIntel" &&
      window.navigator.maxTouchPoints > 1);

  if (isAndroid) {
    return "android";
  }

  if (isIOS) {
    return "ios";
  }

  return "unknown";
};

function DownloadScreen() {
  const { code } = useParams();
  const hasCode = Boolean(code && code.trim());
  const platform = useMemo(() => detectPlatform(), []);
  const storeUrl =
    platform === "ios"
      ? configData.APP_STORE_URL
      : platform === "android"
      ? configData.PLAY_STORE_URL
      : null;
  const showAppStore = platform !== "android";
  const showPlayStore = platform !== "ios";
  const { t } = useTranslation();
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [qrCodeError, setQrCodeError] = useState(null);

  useEffect(() => {
    document.title = t("Download page title");
  }, [t]);

  useEffect(() => {
    let isActive = true;

    if (!hasCode) {
      setQrCodeUrl("");
      setQrCodeError(null);
      return () => {
        isActive = false;
      };
    }

    setQrCodeError(null);

    QRCode.toDataURL(
      code,
      {
        width: 220,
        margin: 1,
        color: {
          dark: "#2F4961",
          light: "#FFFFFF",
        },
      },
      (error, generatedQrCodeUrl) => {
        if (!isActive) {
          return;
        }

        if (error) {
          setQrCodeUrl("");
          setQrCodeError(error);
          return;
        }

        setQrCodeUrl(generatedQrCodeUrl);
      }
    );

    return () => {
      isActive = false;
    };
  }, [code, hasCode]);

  if (storeUrl) {
    window.location.href = storeUrl;
    return null;
  }

  return (
    <div className="download-page">
      <NavForm logoType="gold" />
      <main className="download-screen">
        <section className="download-screen__card">
          <p className="download-screen__eyebrow">{t("Download page eyebrow")}</p>
          <h1 className="download-screen__title">{t("Download page heading")}</h1>
          <p className="download-screen__message">{t("Download page message")}</p>
          <p className="download-screen__helper">{t("Download page helper")}</p>
          <div className="download-screen__actions">
            {showAppStore && (
              <a
                className="download-screen__badge-link"
                href={configData.APP_STORE_URL}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={t("Download page App Store aria")}
              >
                <img
                  className="download-screen__badge"
                  src={appStoreBadge}
                  alt={t("Download page App Store alt")}
                />
              </a>
            )}
            {showPlayStore && (
              <a
                className="download-screen__badge-link"
                href={configData.PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={t("Download page Play Store aria")}
              >
                <img
                  className="download-screen__badge"
                  src={playStoreBadge}
                  alt={t("Download page Play Store alt")}
                />
              </a>
            )}
          </div>
          {hasCode && (
            <div className="download-screen__qr-section">
              <div className="download-screen__qr-frame">
                {qrCodeUrl ? (
                  <img
                    className="download-screen__qr-image"
                    src={qrCodeUrl}
                    alt={t("Download page QR alt")}
                  />
                ) : (
                  <p className="download-screen__qr-status">
                    {qrCodeError
                      ? t("Download page QR error")
                      : t("Download page QR loading")}
                  </p>
                )}
              </div>
              <p className="download-screen__qr-caption">
                {t("Download page QR caption")}
              </p>
              <div className="download-screen__activation-key">
                <p className="download-screen__activation-key-prompt">
                  {t("Download page activation key prompt")}
                </p>
                <pre className="download-screen__activation-key-code">
                  <code>{code}</code>
                </pre>
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default DownloadScreen;
